import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import atcCodeAPI from '../api/atcCodeAPI';

/**
 * Searchable ATC picker for the BIR reference table.
 *
 * The field is OPTIONAL everywhere it is used — a sales/purchase line
 * legitimately has no ATC when no 2306/2307 was issued against it — so the
 * cleared state is a first-class value, not an error to be nagged about.
 *
 * `value` is an AtcCode _id. Rendering a label for it cannot go through
 * .populate('atc'): AtcCode lives on the global connection while VATRecord and
 * PercentageTax are tenant-scoped, so populate throws MissingSchemaError. The
 * label is resolved here instead, by matching the id against the in-force list
 * for the form (see resolveById).
 */

// The in-force list per (formType, payeeClass) changes only when BIR issues a
// circular, so it is fetched once per page life and shared by every row's
// picker — a 5-row encoding grid would otherwise fire five identical requests.
const listCache = new Map();
const inflight = new Map();

function cacheKey(formType, payeeClass) {
    return `${formType || 'ALL'}|${payeeClass || 'ALL'}`;
}

async function loadFullList(formType, payeeClass) {
    const key = cacheKey(formType, payeeClass);
    if (listCache.has(key)) return listCache.get(key);
    if (inflight.has(key)) return inflight.get(key);

    const p = atcCodeAPI
        .list({ formType, payeeClass })
        .then((rows) => {
            listCache.set(key, rows);
            inflight.delete(key);
            return rows;
        })
        .catch((err) => {
            inflight.delete(key);
            throw err;
        });

    inflight.set(key, p);
    return p;
}

const labelOf = (atc) => (atc ? `${atc.code} — ${atc.description}` : '');

const AtcSearchSelect = ({
    formType,
    payeeClass,
    value = null,
    onChange,
    disabled = false,
    placeholder = 'Search ATC code or description…',
    inputId,
}) => {
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [highlight, setHighlight] = useState(-1);
    // The row currently selected, kept so the input can show a label instead of
    // the raw id when the component is mounted against a saved record.
    const [selected, setSelected] = useState(null);

    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const optionRefs = useRef({});
    // Guards the resolve effect against a stale response overwriting a newer
    // selection when `value` changes twice in quick succession.
    const resolveToken = useRef(0);

    // ── Resolve an incoming id to a display label ────────────────────────────
    useEffect(() => {
        if (!value) {
            setSelected(null);
            setQuery('');
            return;
        }
        // Already showing this row — nothing to resolve.
        if (selected && selected._id === value) return;

        const token = ++resolveToken.current;
        let cancelled = false;

        loadFullList(formType, payeeClass)
            .then((rows) => {
                if (cancelled || token !== resolveToken.current) return;
                const hit = (rows || []).find((r) => r._id === value);
                if (hit) {
                    setSelected(hit);
                    setQuery(labelOf(hit));
                } else {
                    // The id is real but no longer in force for this form (a
                    // superseded rate, or the record was filed under a different
                    // form). Say so rather than rendering a bare ObjectId.
                    setSelected(null);
                    setQuery('');
                    setError('Saved ATC is no longer in force for this form.');
                }
            })
            .catch(() => {
                if (cancelled || token !== resolveToken.current) return;
                setError('Could not load ATC codes.');
            });

        return () => {
            cancelled = true;
        };
        // `selected` is deliberately not a dependency: it is written by this
        // effect, and including it would re-run the resolve on every selection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, formType, payeeClass]);

    // ── Search ──────────────────────────────────────────────────────────────
    const runSearch = useCallback(
        async (term) => {
            setLoading(true);
            setError('');
            try {
                // An empty term reuses the cached full list, so simply opening
                // the dropdown costs no request.
                const rows = term.trim()
                    ? await atcCodeAPI.list({ formType, payeeClass, q: term.trim() })
                    : await loadFullList(formType, payeeClass);
                // Refs are keyed by index; a shorter result set would otherwise
                // leave unmounted nodes behind for the scroll effect to read.
                optionRefs.current = {};
                setOptions(rows || []);
                setHighlight((rows || []).length > 0 ? 0 : -1);
            } catch {
                setOptions([]);
                setHighlight(-1);
                setError('Could not load ATC codes.');
            } finally {
                setLoading(false);
            }
        },
        [formType, payeeClass]
    );

    // Debounce: 300ms after the last keystroke. Skipped while the input still
    // shows the selected row's label, so re-focusing a filled field lists
    // everything instead of searching for its own label.
    const isShowingSelectedLabel = Boolean(selected) && query === labelOf(selected);

    useEffect(() => {
        if (!open || isShowingSelectedLabel) return undefined;
        const t = setTimeout(() => runSearch(query), 300);
        return () => clearTimeout(t);
    }, [query, open, isShowingSelectedLabel, runSearch]);

    // ── Close on outside click ──────────────────────────────────────────────
    useEffect(() => {
        if (!open) return undefined;
        const onDocMouseDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                // Abandoning a half-typed search restores the committed value
                // rather than leaving the input showing an unselected term.
                setQuery(selected ? labelOf(selected) : '');
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [open, selected]);

    // Keep the highlighted option scrolled into view during keyboard nav.
    // Purely cosmetic, and scrollIntoView is absent in some environments
    // (jsdom, older embedded webviews) — feature-detect so a missing method
    // cannot take the whole picker down.
    useEffect(() => {
        if (!open || highlight < 0) return;
        const node = optionRefs.current[highlight];
        if (typeof node?.scrollIntoView === 'function') {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight, open]);

    const commit = useCallback(
        (atc) => {
            setSelected(atc);
            setQuery(atc ? labelOf(atc) : '');
            setOpen(false);
            setHighlight(-1);
            setError('');
            onChange?.(atc);
        },
        [onChange]
    );

    const clear = useCallback(() => {
        commit(null);
        inputRef.current?.focus();
    }, [commit]);

    // Bound to both focus and click: after a keyboard commit the input keeps
    // focus, so a click on it fires no focus event — without the click binding
    // the dropdown could not be reopened without blurring first.
    const openDropdown = () => {
        if (disabled) return;
        setOpen(true);
        if (options.length === 0) runSearch('');
    };

    const handleChange = (e) => {
        setQuery(e.target.value);
        setOpen(true);
        // Typing over a committed selection clears it — the field is optional,
        // so a half-typed term must not silently keep the previous id attached.
        if (selected) {
            setSelected(null);
            onChange?.(null);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setOpen(false);
            setQuery(selected ? labelOf(selected) : '');
            return;
        }
        if (!open) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                openDropdown();
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((i) => (options.length === 0 ? -1 : (i + 1) % options.length));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((i) => (options.length === 0 ? -1 : (i - 1 + options.length) % options.length));
        } else if (e.key === 'Enter') {
            // Only swallow Enter when it actually picks something — otherwise the
            // key must still reach the surrounding form.
            if (highlight >= 0 && options[highlight]) {
                e.preventDefault();
                commit(options[highlight]);
            }
        }
    };

    const listboxId = useMemo(
        () => `atc-listbox-${inputId || Math.random().toString(36).slice(2)}`,
        [inputId]
    );

    return (
        <div className="relative" ref={wrapperRef}>
            <div className="relative">
                <input
                    id={inputId}
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    autoComplete="off"
                    disabled={disabled}
                    value={query}
                    placeholder={placeholder}
                    onFocus={openDropdown}
                    onClick={openDropdown}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    className={`w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${
                        selected ? 'pr-8' : ''
                    } ${disabled ? 'bg-ink-canvas text-ink-body cursor-not-allowed' : 'bg-white'}`}
                />

                {selected && !disabled && (
                    <button
                        type="button"
                        onClick={clear}
                        aria-label="Clear ATC"
                        title="Clear ATC"
                        className="absolute inset-y-0 right-0 flex items-center pr-2 text-ink-body hover:text-ink"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {selected?.needsReview && (
                <p className="mt-1 text-xs text-warning-500">
                    Unverified rate — confirm with the accountant before filing.
                </p>
            )}

            {error && <p className="mt-1 text-xs text-danger-500">{error}</p>}

            {open && (
                <ul
                    id={listboxId}
                    role="listbox"
                    className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded border border-ink-line bg-white shadow-sm"
                >
                    {loading && <li className="px-3 py-2 text-sm text-ink-body">Searching…</li>}

                    {!loading && options.length === 0 && (
                        <li className="px-3 py-2 text-sm text-ink-body">
                            {query.trim() ? `No ATC matches “${query.trim()}”.` : 'No ATC codes available.'}
                        </li>
                    )}

                    {!loading &&
                        options.map((o, i) => (
                            <li
                                key={o._id}
                                ref={(el) => {
                                    optionRefs.current[i] = el;
                                }}
                                role="option"
                                aria-selected={i === highlight}
                                // onMouseDown, not onClick: the input's blur would
                                // otherwise close the list before the click lands.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    commit(o);
                                }}
                                onMouseEnter={() => setHighlight(i)}
                                className={`cursor-pointer px-3 py-2 text-sm ${
                                    i === highlight ? 'bg-primary-50 text-primary-700' : 'text-ink'
                                }`}
                            >
                                <div className="font-medium">
                                    {o.code}
                                    <span className="ml-2 font-normal text-ink-body">{o.ratePercent}%</span>
                                    {o.needsReview && (
                                        <span className="ml-2 font-normal text-warning-500">⚠ unverified</span>
                                    )}
                                </div>
                                <div className="text-xs text-ink-body">{o.description}</div>
                            </li>
                        ))}
                </ul>
            )}
        </div>
    );
};

export default AtcSearchSelect;
